import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { FulfillmentContactParty, ProductPlatform } from "client/api-v2";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import type { SectionProps } from "areas/orders/builder/sections/SectionProps";
import type { FulfillmentContactOption } from "areas/orders/orderConstants";
import { fulfillmentContactPartyLabels, noFulfillmentContactParty } from "areas/orders/orderConstants";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

const editableBlurb = "Configure how this subscription can order and how this quote is fulfilled.";
const lockedBlurb = "These settings show how this subscription can order, and how it is fulfilled.";

const selfServeEnabled = "Enabled";
const selfServeDisabled = "Disabled";
const selfServeExplanation =
  "The subscription holds whether self-serve ordering is enabled, so open the subscription to change it.";

const automaticFulfilmentOffHelper =
  "If you clear this box, the order needs manual approval for fulfilment after the customer accepts this quote.";

const licenceRecipientLabel = "Licence recipient";
const licenceRecipientHelper = "Who the licence goes to at fulfilment. The quote prints it as Licence To.";
const licenceRecipientEmptyNote = "No party is chosen, so the quote prints no Licence To line.";
const licenceRecipientNoResellerNote = "Add a reseller to the quote to send the licence to them.";

function namesReseller(party: FulfillmentContactOption): boolean {
  return party === FulfillmentContactParty.Reseller || party === FulfillmentContactParty.Both;
}

const fulfillmentContactParties = Object.values(FulfillmentContactParty);
const licenceRecipientOptions: FulfillmentContactOption[] = [noFulfillmentContactParty, ...fulfillmentContactParties];

function LicenceRecipient({
  selected,
  hasReseller,
  readOnly,
  onChange,
}: {
  selected: FulfillmentContactParty | null;
  hasReseller: boolean;
  readOnly: boolean;
  onChange: (party: FulfillmentContactParty | null) => void;
}) {
  const labelId = React.useId();

  return (
    <FormControl>
      <FormLabel id={labelId} sx={{ typography: "subtitle2" }}>
        {licenceRecipientLabel}
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={labelId}
        value={selected ?? noFulfillmentContactParty}
        onChange={(_event, value) => onChange(toFulfillmentContactParty(value))}
      >
        {licenceRecipientOptions.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio size="small" disabled={readOnly || (!hasReseller && namesReseller(option))} />}
            label={fulfillmentContactPartyLabels[option]}
            slotProps={{ typography: { variant: "body2" } }}
          />
        ))}
      </RadioGroup>
      <Typography variant="caption" color="text.secondary">
        {licenceRecipientHelper}
      </Typography>
      {selected === null && (
        <Typography variant="caption" color="text.secondary">
          {licenceRecipientEmptyNote}
        </Typography>
      )}
      {!hasReseller && !readOnly && (
        <Typography variant="caption" color="text.secondary">
          {licenceRecipientNoResellerNote}
        </Typography>
      )}
    </FormControl>
  );
}

function toFulfillmentContactParty(value: string): FulfillmentContactParty | null {
  return fulfillmentContactParties.find((party) => party === value) ?? null;
}

export function OrderProcessingSection({ order, draft, setField, readOnly }: SectionProps) {
  return (
    <BuilderSection title="Subscription order processing">
      <Typography variant="body2" color="text.secondary">
        {readOnly ? lockedBlurb : editableBlurb}
      </Typography>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Subscription configuration</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Self-serve
          </Typography>
          <Typography variant="body2" component="span">
            {order.subscriptionSelfServiceEnabled ? selfServeEnabled : selfServeDisabled}
          </Typography>
          <Link component={RouterLink} to={subscriptionRoutes.detail(order.subscriptionId).root} variant="body2">
            Edit on subscription
          </Link>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {selfServeExplanation}
        </Typography>
      </Stack>

      <Divider />

      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Quote configuration</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={!draft.requiresManualFulfillmentApproval}
              disabled={readOnly}
              size="small"
              onChange={(event) => setField("requiresManualFulfillmentApproval", !event.target.checked)}
            />
          }
          label="Automatic fulfilment"
          slotProps={{ typography: { variant: "body2" } }}
        />
        <Typography variant="caption" color="text.secondary">
          {automaticFulfilmentOffHelper}
        </Typography>
      </Stack>

      {order.platform === ProductPlatform.OctopusServer && (
        <>
          <Divider />
          <LicenceRecipient
            selected={draft.fulfillmentContact}
            hasReseller={!!draft.resellerBillingAccountCode?.trim()}
            readOnly={readOnly}
            onChange={(party) => setField("fulfillmentContact", party)}
          />
        </>
      )}
    </BuilderSection>
  );
}
